// اجزای مشترک AP-INV-01 (SCR-STAFF-870، 877): کلیدهای Query، فهرست‌های شمارشی، رنگ وضعیت بچ، متن معادل واحدها و Hookهای دسته و تأمین‌کننده.
import type {
  InvBatchStatus,
  InvItemType,
  InvItemUnit,
  InvItemUnitConversionInfo,
  InvSupplierType,
} from '@phoenix/api-client';
import { unwrap } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { faDigits, type StatusTone } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';

export const ITEMS_PATH = '/inventory/items';
export const SUPPLIERS_PATH = '/inventory/suppliers';

export const ITEM_TYPES: readonly InvItemType[] = ['Consumable', 'Sellable', 'ConsumableAndSellable', 'Medication', 'SparePart'];
export const ITEM_UNITS: readonly InvItemUnit[] = [
  'Piece',
  'Box',
  'Pack',
  'Carton',
  'Vial',
  'Ampoule',
  'Syringe',
  'Bottle',
  'Tube',
  'Jar',
  'Sachet',
  'Kit',
  'Cartridge',
  'Roll',
  'Pair',
  'Unit',
  'Milliliter',
  'Gram',
];
export const SUPPLIER_TYPES: readonly InvSupplierType[] = ['Goods', 'TechnicalServices', 'Both'];

export const inventoryKeys = {
  categories: (tenantId: string | undefined) => ['inventory', 'categories', tenantId] as const,
  items: (tenantId: string | undefined) => ['inventory', 'items', tenantId] as const,
  item: (tenantId: string | undefined, id: string) => ['inventory', 'items', tenantId, id] as const,
  batches: (tenantId: string | undefined, id: string) => ['inventory', 'items', tenantId, id, 'batches'] as const,
  suppliers: (tenantId: string | undefined) => ['inventory', 'suppliers', tenantId] as const,
};

/** رنگ وضعیت بچ (SM-Batch). */
export function batchTone(status: InvBatchStatus): StatusTone {
  switch (status) {
    case 'Active':
      return 'active';
    case 'Expiring':
      return 'pending';
    default:
      return 'cancelled';
  }
}

/** نوع فروشی است. */
export const isSellable = (type: InvItemType) => type === 'Sellable' || type === 'ConsumableAndSellable';

/**
 * معادل یک واحد غیرپایه با واحد پایه به‌صورت متن (BR-INV-003): «۱ جعبه = ۱۰ ویال»؛ برای واحد کوچک‌تر از پایه «۱ ویال = ۱۰۰ واحد».
 */
export function conversionText(
  conversion: InvItemUnitConversionInfo,
  baseUnit: InvItemUnit,
  unitLabel: (unit: InvItemUnit) => string,
): string {
  const unitQuantity = Number(conversion.unitQuantity);
  const baseQuantity = Number(conversion.baseQuantity);
  if (baseQuantity === 1 && unitQuantity > 1) {
    return `${faDigits(1)} ${unitLabel(baseUnit)} = ${faDigits(unitQuantity)} ${unitLabel(conversion.unit)}`;
  }
  return `${faDigits(unitQuantity)} ${unitLabel(conversion.unit)} = ${faDigits(baseQuantity)} ${unitLabel(baseUnit)}`;
}

/**
 * تبدیل دقیق مقدار صحیح بین دو واحد (هم‌ارز UnitConverter سرور)؛ نتیجه غیرصحیح ← null. مثال AC-INV-002.1: ۲ جعبه ← ۲۰ ویال ← ۲۰۰۰ واحد.
 */
export function convertQuantity(
  quantity: number,
  from: InvItemUnit,
  to: InvItemUnit,
  baseUnit: InvItemUnit,
  conversions: readonly InvItemUnitConversionInfo[],
): number | null {
  const factor = (unit: InvItemUnit): [number, number] | null => {
    if (unit === baseUnit) {
      return [1, 1];
    }
    const found = conversions.find((conversion) => conversion.unit === unit);
    return found ? [Number(found.unitQuantity), Number(found.baseQuantity)] : null;
  };
  const source = factor(from);
  const target = factor(to);
  if (!source || !target) {
    return null;
  }
  const numerator = quantity * source[1] * target[0];
  const denominator = source[0] * target[1];
  return numerator % denominator === 0 ? numerator / denominator : null;
}

/** دسته‌های کالا. */
export function useItemCategories() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: inventoryKeys.categories(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/item-categories')),
  });
}

/** تأمین‌کنندگان فعال برای انتخاب (فقط با inv.supplier.manage). */
export function useSupplierOptions() {
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  return useQuery({
    queryKey: [...inventoryKeys.suppliers(session?.tenantId), 'options'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/suppliers', { params: { query: { active: true, limit: 100 } } })),
    enabled: access.can('inv.supplier.manage'),
  });
}
